import httpClient from '@common/api/httpClient';
import paramsSerializer from '@common/api/paramsSerializer';
import {
  type LegacyUserResponse,
  type ProjectionUpdateRequest,
  type RegisterResponse,
} from '@common/types/api';
import { type InstallationId } from '@common/types/brandedTypes';
import { type FilterSuggestion } from '@common/types/deviceFilters';
import { createRegisterFromResponse } from '@common/types/models/Register';

export enum SpecialValueStateQueryParameter {
  Disabled,
  NotSupported,
  SwitchedOff,
}

export async function fetchRegister(installationId: InstallationId, registerIndex: number) {
  try {
    const response = await httpClient.get<RegisterResponse>(
      `/rest/v1/Registers/Installations/${installationId}/RegisterIndexes/${registerIndex}`,
    );
    return createRegisterFromResponse(response.data);
  } catch {
    return undefined;
  }
}

export async function fetchValueDefinitions(
  type: string,
  filter: string,
): Promise<FilterSuggestion[]> {
  const { data } = await httpClient.get<{
    definition: string;
  }[]>(
    '/rest/v1/ValueDefinitions',
    { params: { type, filter } },
  );
  try {
    const distinctValues = new Set<string>(data.map(({ definition }) => definition));
    return Array.from(distinctValues)
      .map((definition) => ({ id: definition, value: definition }))
      .sort((a, b) => a.value.localeCompare(b.value));
  } catch {
    return [];
  }
}

/** @throws {ApiError} `not-found` when no legacy user matches the login and MAC address. */
export async function validateMapping(
  userName: string,
  macAddress: string,
) {
  const { data } = await httpClient.get<LegacyUserResponse>(
    '/rest/v1/LegacyUsers',
    {
      params: {
        userName,
        macAddress,
      },
      paramsSerializer,
    },
  );
  return data;
}

export interface UpdateRegisterProps {
  installationId: InstallationId;
  registerIndex: string;
  registerValue: string;
  clientUuid: string;
}

/** @throws {ApiError} when the server refuses the value. */
export async function updateRegister({
  installationId,
  registerIndex,
  registerValue,
  clientUuid,
}: UpdateRegisterProps): Promise<void> {
  await httpClient.post(
    `/rest/v1/Registers/Installations/${installationId}/Registers`,
    { registerIndex, clientUuid, registerValue: `${parseFloat(registerValue)}` },
  );
}

export interface UpdateProjectionProps {
  installationId: InstallationId;
  projectionFieldId: string;
  value: string;
}

export async function updateProjections(
  installationId: InstallationId,
  request: ProjectionUpdateRequest,
): Promise<void> {
  await httpClient.put(`/rest/v1/Projections/Installations/${installationId}`, request);
}

/** @throws {ApiError} when the server refuses the value. */
export async function updateProjection({
  installationId,
  projectionFieldId,
  value,
}: UpdateProjectionProps): Promise<void> {
  await updateProjections(
    installationId,
    {
      fields: [{
        projectionFieldId: Number(projectionFieldId),
        items: [{ key: 'Value', value }],
      }],
    },
  );
}
